import React, { FC, useEffect, useRef } from 'react';
import { SideMenu } from '@components/SideMenu';
import { useProject } from '@context/Project/ProjectProvider';
import { Button, Chip, IconButton, Stack, Typography } from '@mui/material';
import { IActorTypes } from '@store/services/nodes/types';
import { themePalette } from '@theme/muiTheme';
import {
  CloseIcon,
  CompletenessIcon,
  DependencyIcon,
  FocusOnIcon,
  KeyControlsIcon,
  MaturityIcon,
  ModelEvolutionIcon,
} from '@utils/iconsDefs';

export enum DrawerBadges {
  FocusOn = 'Focus On',
  Dependency = 'Dependency',
  Maturity = 'Maturity',
  ModelEvolution = 'Model evolution',
  Completeness = 'Completeness',
  KeyControls = 'Key controls',
}

export type ProjectRightDrawerProps = {
  title: string;
  onClose?: () => void;
  onSave?: (options?: { fromShortcut?: boolean }) => void | Promise<void>;
  disabledSave?: boolean;
  children?: React.ReactNode;
  headChildren?: React.ReactNode;
  tab?: number;
  badge?: DrawerBadges;
  tag?: IActorTypes;
};

const Badges = {
  [DrawerBadges.FocusOn]: (
    <Chip
      label="Focus on"
      color="primary"
      icon={<FocusOnIcon fill={themePalette.grey[900]} />}
    />
  ),
  [DrawerBadges.Dependency]: (
    <Chip
      label="Dependency"
      color="primary"
      icon={<DependencyIcon fill={themePalette.grey[900]} />}
    />
  ),
  [DrawerBadges.Maturity]: (
    <Chip
      label="Maturity"
      color="primary"
      icon={<MaturityIcon fill={themePalette.grey[900]} />}
    />
  ),
  [DrawerBadges.ModelEvolution]: (
    <Chip
      label="Model evolution"
      color="primary"
      icon={<ModelEvolutionIcon fill={themePalette.grey[900]} />}
    />
  ),
  [DrawerBadges.Completeness]: (
    <Chip
      label="Completeness"
      color="primary"
      icon={<CompletenessIcon fill={themePalette.grey[900]} />}
    />
  ),
  [DrawerBadges.KeyControls]: (
    <Chip
      label="Key controls"
      color="primary"
      icon={<KeyControlsIcon fill={themePalette.grey[900]} />}
    />
  ),
};

export const Tags: Partial<Record<IActorTypes, React.ReactNode>> = {
  [IActorTypes.CreatedByApi]: <Chip label="Created via API" color="primary" />,
  [IActorTypes.UpdatedByApi]: <Chip label="Updated via API" color="primary" />,
  [IActorTypes.User]: undefined,
};

export const DRAWER_WIDTH = 415;

const ProjectRightDrawer: FC<ProjectRightDrawerProps> = ({
  title,
  onClose,
  onSave,
  disabledSave,
  children,
  headChildren,
  tab,
  badge,
  tag,
}) => {
  const { viewOnly, isUnsavedModalOpen } = useProject();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    ref.current?.scrollTo({ top: 0 });
  }, [tab]);

  const settingsRef = useRef({
    isUnsavedModalOpen,
    disabledSave,
  });

  useEffect(() => {
    settingsRef.current = {
      isUnsavedModalOpen,
      disabledSave,
    };
  }, [isUnsavedModalOpen, disabledSave]);

  useEffect(() => {
    const trySave = (isComboboxOpen: boolean, fromShortcut: boolean) => {
      const isSaveAvailable =
        !settingsRef.current.isUnsavedModalOpen &&
        !settingsRef.current.disabledSave &&
        !isComboboxOpen;

      if (isSaveAvailable)
        Promise.resolve(onSave?.({ fromShortcut })).catch(() => undefined);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const focusedElement = document.activeElement as HTMLElement | null;

      const isComboboxOpen =
        target?.role === 'combobox' && target?.ariaExpanded === 'true';

      const isInputFocused =
        !!focusedElement &&
        (focusedElement.tagName === 'INPUT' ||
          focusedElement.tagName === 'TEXTAREA' ||
          focusedElement.isContentEditable);

      const isSaveShortcut =
        (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's';

      const isEnterSave = e.key === 'Enter' && !e.shiftKey;

      // Shift+Enter stays as line break in textarea
      if (e.key === 'Enter' && e.shiftKey) return;

      if (!isSaveShortcut && !isEnterSave) return;

      // prevent browser "save page" and default submit behavior
      e.preventDefault();

      const fromShortcut = isSaveShortcut;

      if (isSaveShortcut) {
        if (isComboboxOpen) {
          (target as HTMLElement).blur();
          setTimeout(() => trySave(false, fromShortcut), 10);
        } else {
          trySave(isComboboxOpen, fromShortcut);
        }
        return;
      }

      // let RHF/yup onBlur validation update form state
      if (isInputFocused) {
        (focusedElement as HTMLElement).blur();
        setTimeout(() => {
          trySave(isComboboxOpen, fromShortcut);
        }, 10);
        return;
      }
      trySave(isComboboxOpen, fromShortcut);
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <SideMenu
      open
      side="right"
      width={DRAWER_WIDTH}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'white',
        zIndex: 10,
      }}
    >
      <Stack p={6} gap={6}>
        <Stack direction="row" justifyContent="space-between">
          <Stack gap={2} alignItems="flex-start">
            {badge && Badges[badge]}

            <Typography variant="h2">{title}</Typography>

            {tag && Tags[tag]}
          </Stack>

          <IconButton onClick={onClose} sx={{ right: -0, p: '6px' }}>
            <CloseIcon fill={themePalette.grey[900]} />
          </IconButton>
        </Stack>

        {headChildren}
      </Stack>

      <Stack ref={ref} px={6} pb={6} flex={1} gap={6} overflow="auto">
        {children}
      </Stack>

      {!viewOnly && (
        <Stack
          p={6}
          gap={4}
          direction="row"
          borderTop={`1px solid ${themePalette.grey[500]}`}
        >
          <Button
            variant="outlined"
            size="large"
            sx={{ flex: 1 }}
            onClick={onClose}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            size="large"
            sx={{ flex: 1 }}
            disabled={disabledSave}
            onClick={() => onSave?.({ fromShortcut: false })}
          >
            Save
          </Button>
        </Stack>
      )}
    </SideMenu>
  );
};

export default ProjectRightDrawer;
